/**
 * Session and permission access for components.
 *
 * Wraps the `auth` slice so components never reach into store shape directly —
 * `useAuth().can('leads','create')` reads as the question being asked, and the
 * slice can be reorganised without touching every screen.
 */
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { selectAuth, signOut } from '../features/auth/authSlice.js';

/** Data scopes, narrowest first — the order `iam.data_scope` declares. */
const SCOPE_ORDER = ['none', 'own', 'team', 'territory', 'org', 'all'];

export function useAuth() {
  const auth = useSelector(selectAuth);
  const dispatch = useDispatch();

  /**
   * May the user perform `operation` on `screen`?
   *
   * **Advisory only.** This decides whether to render a control. The API asks
   * `iam.can()` again on every request and row-level security filters
   * independently, so a bug here can hide a button or show one that 403s — it
   * can never grant access.
   */
  const can = useCallback(
    (screen, operation) => Boolean(auth.screens[screen]?.operations?.includes(operation)),
    [auth.screens],
  );

  /**
   * True if the user holds any operation at all on `screen` — i.e. may open it.
   *
   * Presence in the map is **not** the test. `/auth/me` returns every screen the
   * caller's roles touch, including ones reached only by a deny, with an empty
   * `operations` array — §2 shows exactly that, and it is what lets an admin UI
   * say "forbidden" rather than "no such screen". Reading presence as access put
   * all eight Administration entries in the executive's sidebar: the exec role
   * denies every mutating verb on every screen, so every screen in the product
   * came back in its payload.
   */
  const canOpen = useCallback(
    (screen) => (auth.screens[screen]?.operations?.length ?? 0) > 0,
    [auth.screens],
  );

  /** The widest data scope held for `screen`, or 'none'. */
  const scopeOf = useCallback(
    (screen) => auth.screens[screen]?.data_scope ?? 'none',
    [auth.screens],
  );

  /** True when the user's scope on `screen` is at least `minimum`. */
  const hasScope = useCallback(
    (screen, minimum) => SCOPE_ORDER.indexOf(scopeOf(screen)) >= SCOPE_ORDER.indexOf(minimum),
    [scopeOf],
  );

  /**
   * Field-level access: 'write', 'read' or 'hidden'.
   *
   * Defaults to 'write' when no rule exists, matching the server — field
   * permissions are a restriction layered on top of an operation the user
   * already holds, not a grant in their own right.
   */
  const fieldAccess = useCallback(
    (entity, field) => auth.fieldPermissions?.[entity]?.[field] ?? 'write',
    [auth.fieldPermissions],
  );

  const roleCodes = useMemo(() => auth.roles.map((r) => r.code), [auth.roles]);

  return {
    ...auth,
    isAuthenticated: auth.status === 'authenticated',
    isResolving: auth.status === 'unknown' || auth.status === 'loading',
    roleCodes,
    can,
    canOpen,
    scopeOf,
    hasScope,
    fieldAccess,
    signOut: (allDevices = false) => dispatch(signOut(allDevices)),
  };
}
